import React, { useState, useRef, useEffect } from 'react';
import { FloatingWhatsApp } from '@digicroz/react-floating-whatsapp';
import { Mail, Phone, MessageCircle, X, Instagram, Shield, GraduationCap, Clock, CheckCircle } from 'lucide-react';
import { createEssayOrder, createCourseworkInquiry, createPayment } from './api/orders';

/* ─── PRICING (pure functions, no state) ──────────────────── */
const deadlineRates    = { '24hrs': 22, '48hrs': 18, '3days': 15, '7days': 13 };
const levelMultipliers = { highschool: 1.0, undergraduate: 1.1, masters: 1.25, phd: 1.4 };

const calcPrice = (deadline, pages, level) => {
  const rate = deadlineRates[deadline] || 13;
  const mult = levelMultipliers[level]  || 1;
  const p    = pages === '' || !pages   ? 1 : Number(pages);
  return (rate * p * mult).toFixed(2);
};

/* ─── STYLE TOKENS ────────────────────────────────────────── */
const T = {
  brown:  '#92400e',
  darkbr: '#78350f',
  cream:  '#fffbeb',
  sand:   '#fef3c7',
  stone:  '#e7e5e4',
  muted:  '#78716c',
  dark:   '#1c1917',
  mid:    '#44403c',
  font:   '"Palatino Linotype","Book Antiqua",Palatino,Georgia,serif',
  sans:   '"Helvetica Neue",Arial,sans-serif',
};

const chipStyle = {
  display: 'inline-block', background: T.sand, color: T.brown,
  borderRadius: 999, padding: '5px 16px', fontSize: 12,
  fontWeight: 700, letterSpacing: '0.06em', marginBottom: 16,
};
const sectionHeading = {
  fontFamily: T.font, fontSize: 'clamp(28px,3.5vw,42px)',
  fontWeight: 900, color: T.dark, margin: '0 0 12px',
};
const btnPrimary = (extra = {}) => ({
  background: `linear-gradient(135deg,${T.brown},${T.darkbr})`,
  color: '#fff', borderRadius: 999, fontWeight: 700, fontSize: 16,
  border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(120,53,15,.30)',
  padding: '14px 32px', ...extra,
});
const inpStyle = (extra = {}) => ({
  width: '100%', padding: '12px 16px',
  border: `2px solid ${T.stone}`, borderRadius: 10,
  fontSize: 15, color: T.dark, outline: 'none',
  boxSizing: 'border-box', fontFamily: T.sans, background: '#fff', ...extra,
});

/* ═══════════════════════════════════════════════════════════
   CALCULATOR — completely self-contained with its OWN state.
   Parent never re-renders when user types pages / picks deadline.
   This is the only correct fix for layout shift.
═══════════════════════════════════════════════════════════ */
const Calculator = ({ onOrder }) => {
  const [academicLevel, setAcademicLevel] = useState('highschool');
  const [deadline,      setDeadline]      = useState('7days');
  const [pages,         setPages]         = useState(1);

  const handlePageInput = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val > 0) setPages(val);
    else if (e.target.value === '') setPages('');
  };

  const price = calcPrice(deadline, pages, academicLevel);

  const deadlineOptions = [
    { key: '24hrs', label: '24 Hours', rate: 22 },
    { key: '48hrs', label: '48 Hours', rate: 18 },
    { key: '3days', label: '3 Days',   rate: 15 },
    { key: '7days', label: '7+ Days',  rate: 13 },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-stone-200">
      <h3 className="text-2xl font-bold text-stone-900 mb-6" style={{ fontFamily: T.font }}>
        Calculate Your Price
      </h3>

      <div className="space-y-5">

        {/* Academic Level */}
        <div>
          <label className="block text-sm font-semibold text-stone-600 mb-2">Academic Level</label>
          <select
            value={academicLevel}
            onChange={e => setAcademicLevel(e.target.value)}
            className="w-full px-4 py-3 border-2 border-stone-300 rounded-lg focus:border-amber-600 focus:outline-none bg-white text-stone-800"
          >
            <option value="highschool">High School</option>
            <option value="undergraduate">Undergraduate</option>
            <option value="masters">Masters</option>
            <option value="phd">PhD</option>
          </select>
        </div>

        {/* Pages */}
        <div>
          <label className="block text-sm font-semibold text-stone-600 mb-2">Number of Pages</label>
          <input
            type="number"
            min="1"
            value={pages}
            onChange={handlePageInput}
            className="w-full px-4 py-3 border-2 border-stone-300 rounded-lg focus:border-amber-600 focus:outline-none text-stone-800"
          />
          <p className="text-xs text-stone-400 mt-1">1 page = 275 words</p>
        </div>

        {/* Deadline buttons */}
        <div>
          <label className="block text-sm font-semibold text-stone-600 mb-2">Deadline</label>
          <div className="grid grid-cols-2 gap-3">
            {deadlineOptions.map(({ key, label, rate }) => {
              const active = deadline === key;
              const btnPrice = calcPrice(key, pages, academicLevel);
              return (
                <button
                  key={key}
                  onClick={() => setDeadline(key)}
                  className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                    active
                      ? 'bg-amber-700 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  <div>{label}</div>
                  {/* tabular-nums + fixed min-width stops ANY width shift as digits change */}
                  <div className={`text-xs mt-1 tabular-nums ${active ? 'text-amber-200' : 'text-stone-400'}`}>
                    ${btnPrice}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Total — min-w keeps box from collapsing or expanding */}
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-5 border-2 border-amber-200">
          <div className="flex justify-between items-center">
            <span className="text-base font-semibold text-stone-600">Total Price:</span>
            <span className="text-4xl font-bold text-amber-700 tabular-nums min-w-[150px] text-right"
              style={{ fontFamily: T.font }}>
              ${price}
            </span>
          </div>
        </div>

        <button
          onClick={() => onOrder({ academicLevel, deadline, pages, price })}
          className="w-full bg-amber-700 text-white py-4 rounded-xl font-bold text-lg hover:bg-amber-800 transition shadow-lg"
        >
          Continue to Order →
        </button>

      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN APP
═══════════════════════════════════════════════════════════ */
export default function EssayMonkApp() {
  const [currentPage,       setCurrentPage]   = useState('home');
  const [orderContext,      setOrderContext]   = useState(null);   // set when user clicks "Order"
  const [activeSection,     setActiveSection] = useState('hero');
;

  /* ── section refs for smooth scroll ── */
  const sectionRefs = {
    hero:           useRef(null),
    services:       useRef(null),
    'how-it-works': useRef(null),
    testimonials:   useRef(null),
    about:          useRef(null),
    faq:            useRef(null),
  };

  const scrollTo = (section) => {
    if (currentPage !== 'home') {
      setCurrentPage('home');
      setTimeout(() => {
        sectionRefs[section]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(section);
      }, 80);
    } else {
      sectionRefs[section]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(section);
    }
  };

  /* IntersectionObserver — highlight active nav link */
  useEffect(() => {
    if (currentPage !== 'home') return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.dataset.sec); }),
      { rootMargin: '-25% 0px -65% 0px' }
    );
    Object.entries(sectionRefs).forEach(([key, ref]) => {
      if (ref.current) { ref.current.dataset.sec = key; obs.observe(ref.current); }
    });
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleOrderClick = (ctx) => {
    setOrderContext(ctx);
    setCurrentPage('order');
  };

  /* ── NAV ─────────────────────────────────────────────────── */
  const NavBar = () => {
    const links = [
      { key: 'hero',         label: 'Home'         },
      { key: 'services',     label: 'Services'     },
      { key: 'how-it-works', label: 'How It Works' },
      { key: 'about',        label: 'About Us'     },
      { key: 'faq',          label: 'FAQ'          },
    ];
    return (
      <nav className="sticky top-0 z-50 shadow-md"
        style={{ background: `linear-gradient(90deg,${T.darkbr},#57534e)` }}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16">

          <div onClick={() => scrollTo('hero')}
            className="flex items-center gap-2 cursor-pointer">
            <span className="text-2xl">🧘</span>
            <span className="text-white font-bold text-xl" style={{ fontFamily: T.font }}>
              EssayMonk
            </span>
          </div>

          <div className="hidden md:flex gap-7">
            {links.map(({ key, label }) => {
              const isActive = currentPage === 'home' && activeSection === key;
              return (
                <button key={key} onClick={() => scrollTo(key)}
                  className="text-sm font-medium transition-all"
                  style={{
                    background: 'none', border: 'none', color: '#fff',
                    cursor: 'pointer', opacity: isActive ? 1 : 0.72,
                    borderBottom: isActive ? '2px solid #fbbf24' : '2px solid transparent',
                    paddingBottom: 2,
                  }}>
                  {label}
                </button>
              );
            })}
          </div>

          <button onClick={() => setCurrentPage('order')}
            className="bg-white font-bold text-sm px-5 py-2 rounded-full hover:bg-amber-50 transition"
            style={{ color: T.brown }}>
            Order Now
          </button>
        </div>
      </nav>
    );
  };

  /* ── HOME (all sections) ─────────────────────────────────── */
  const HomePage = () => {
    const services = [
      { icon:'📝', title:'Essays',              desc:'Argumentative, persuasive, narrative essays',   features:['High school to PhD','All citation styles','24hr rush available'] },
      { icon:'📊', title:'Research Papers',     desc:'In-depth research with proper methodology',     features:['Original research','Data analysis','Literature review'] },
      { icon:'🎓', title:'Dissertations',       desc:'Complete dissertation support',                 features:['Chapter by chapter','Statistical analysis','Unlimited revisions'] },
      { icon:'💼', title:'Case Studies',        desc:'Business and medical case studies',             features:['MBA level','SWOT analysis','Recommendations'] },
      { icon:'📚', title:'Coursework Help',     desc:'Complete course management',                   features:['Full semester','All assignments','Exam prep'] },
      { icon:'✏️', title:'Editing & Proofreading', desc:'Polish your work to perfection',            features:['Grammar check','Structure review','Citation formatting'] },
    ];

    const steps = [
      { n:1, title:'Fill Out the Order Form',  desc:'Provide your topic, length, deadline, and any special requirements.' },
      { n:2, title:'Make Secure Payment',      desc:'Pay safely via PayPal or credit card.' },
      { n:3, title:'Writer Gets to Work',      desc:'We match you with the best writer for your subject and level.' },
      { n:4, title:'Receive Your Paper',       desc:'Get your completed paper before the deadline. Free revisions included.' },
    ];

    const faqs = [
      { q:'Is your service legal?',                    a:'Yes. We provide academic assistance and model papers students can use as reference material.' },
      { q:'Can you assist with a complete degree program?', a:'Yes, we can support you through the entire degree, handling all courses from start to finish.' },
      { q:'How do you ensure originality?',            a:'Every paper is written from scratch. We provide a free plagiarism report with every order.' },
      { q:"What if I'm not satisfied?",                a:"Unlimited free revisions until you're happy. If still unsatisfied, we offer a money-back guarantee." },
      { q:'How quickly can you deliver?',              a:'As fast as 24 hours for rush orders. Standard delivery is 3–7 days.' },
      { q:'Who will write my paper?',                  a:'A qualified writer matched to your subject area and academic level.' },
    ];

    return (
      <div>

        {/* ═══ HERO ══════════════════════════════════════════ */}
        <section ref={sectionRefs.hero}
          className="py-20 px-6"
          style={{ background: `linear-gradient(135deg,${T.cream} 0%,#f5f5f4 55%,${T.sand} 100%)` }}>

          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-start">

            

            {/* Col 1: Headline + badges */}
            <div className="space-y-7 pt-2">
              <span style={chipStyle}>⭐ 4.9 / 5 from 500+ students</span>

              <h1 style={{ fontFamily: T.font }}
                className="text-5xl lg:text-6xl font-bold leading-tight text-stone-900">
                Your Academic<br />
                <span className="italic" style={{ color: T.brown }}>Success,</span><br />
                Our Mission.
              </h1>

              <p className="text-lg text-stone-500 max-w-lg leading-relaxed">
                Expert writing help from qualified professionals. Quality papers,
                on-time delivery, and grades you can be proud of.
              </p>

              <div className="flex flex-wrap gap-3 pt-1">
                <button onClick={() => setCurrentPage('order')} style={btnPrimary()}>
                  Order Essay Now
                </button>
                <button onClick={() => setCurrentPage('coursework')}
                  className="font-bold text-base px-8 py-3.5 rounded-full border-2 transition hover:bg-amber-50"
                  style={{ color: T.brown, borderColor: T.brown, background: '#fff' }}>
                  Get Coursework Help
                </button>
              </div>

              <div className="grid grid-cols-2 gap-5 pt-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: T.brown }} />
                  <div>
                    <p className="font-bold text-stone-900 text-sm">Confidential</p>
                    <p className="text-xs text-stone-400 mt-0.5">Your data is fully private</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <GraduationCap className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: T.brown }} />
                  <div>
                    <p className="font-bold text-stone-900 text-sm">Expert Writers</p>
                    <p className="text-xs text-stone-400 mt-0.5">PhD-qualified professionals</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: T.brown }} />
                  <div>
                    <p className="font-bold text-stone-900 text-sm"> Average delivery </p>
                    <p className="text-xs text-stone-400 mt-0.5">24 hrs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: T.brown }} />
                  <div>
                    <p className="font-bold text-stone-900 text-sm"> 100% </p>
                    <p className="text-xs text-stone-400 mt-0.5"> AI Free and Plagiarism Free </p>
                  </div>
                </div>
              </div>

              </div>

            {/* Col 2: self-contained Calculator */}
            <Calculator onOrder={handleOrderClick} />

          </div>
        </section>

        {/* ═══ SERVICES ══════════════════════════════════════ */}
        <section ref={sectionRefs.services} className="py-24 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span style={chipStyle}>WHAT WE OFFER</span>
              <h2 style={sectionHeading}>Our Services</h2>
              <p className="text-stone-400 text-lg max-w-lg mx-auto">
                Comprehensive academic writing services tailored to your needs
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s, i) => (
                <div key={i}
                  className="border-2 border-stone-200 rounded-2xl p-7 transition-all duration-200 hover:-translate-y-1"
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.brown; e.currentTarget.style.boxShadow = '0 8px 32px rgba(120,53,15,.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.stone; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div className="text-4xl mb-4">{s.icon}</div>
                  <h3 className="font-bold text-lg text-stone-900 mb-2">{s.title}</h3>
                  <p className="text-stone-400 text-sm mb-4 leading-relaxed">{s.desc}</p>
                  <ul className="space-y-2">
                    {s.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-stone-500 text-sm">
                        <span className="text-green-500 font-bold">✓</span>{f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ HOW IT WORKS ══════════════════════════════════ */}
        <section ref={sectionRefs['how-it-works']} className="py-24 px-6"
          style={{ background: `linear-gradient(135deg,${T.cream},#f5f5f4)` }}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <span style={chipStyle}>THE PROCESS</span>
              <h2 style={sectionHeading}>How It Works</h2>
              <p className="text-stone-400 text-lg">Getting your paper is as easy as 1-2-3-4</p>
            </div>
            <div className="space-y-5">
              {steps.map(step => (
                <div key={step.n} className="flex items-center gap-5">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center font-black text-xl text-white flex-shrink-0"
                    style={{ background: `linear-gradient(135deg,${T.brown},${T.darkbr})`,
                      boxShadow: '0 4px 16px rgba(120,53,15,.30)' }}>
                    {step.n}
                  </div>
                  <div className="bg-white border-2 border-stone-200 rounded-2xl px-7 py-5 flex-1">
                    <h3 className="font-bold text-stone-900 mb-1">{step.title}</h3>
                    <p className="text-stone-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <button onClick={() => setCurrentPage('order')} style={btnPrimary({ fontSize: 17 })}>
                Get Started Now
              </button>
            </div>
          </div>
        </section>

        {/* ═══ TESTIMONIALS ══════════════════════════════════ */}
        <section ref={sectionRefs.testimonials} className="py-24 px-6 bg-stone-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span style={chipStyle}>STUDENT REVIEWS</span>
              <h2 style={sectionHeading}>What Our Students Say</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { initials:'SK', name:'Sarah K.',   role:'MBA Student, Harvard',    bg: T.brown,   text:"Amazing service! Got an A on my MBA case study. The writer understood exactly what I needed and delivered 2 days early!" },
                { initials:'JM', name:'James M.',   role:'Undergraduate, NYU',       bg:'#44403c',  text:"Saved my semester! Had 3 papers due the same week and EssayMonk handled them all perfectly. Highly recommend!" },
                { initials:'AL', name:'Amanda L.',  role:'PhD Candidate, Stanford',  bg:'#166534',  text:"Professional, fast, and excellent quality. They helped me with my dissertation and I couldn't be happier!" },
              ].map(t => (
                <div key={t.name} className="bg-white rounded-2xl border-2 border-stone-200 p-7 shadow-sm">
                  <div className="text-amber-400 mb-3 text-lg">⭐⭐⭐⭐⭐</div>
                  <p className="text-stone-500 leading-relaxed mb-5 text-sm">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0"
                      style={{ background: t.bg }}>{t.initials}</div>
                    <div>
                      <p className="font-bold text-stone-800 text-sm">{t.name}</p>
                      <p className="text-xs text-stone-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ ABOUT ═════════════════════════════════════════ */}
        <section ref={sectionRefs.about} className="py-24 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span style={chipStyle}>OUR STORY</span>
              <h2 style={sectionHeading}>About EssayMonk</h2>
              <p className="text-stone-400 text-lg">Your trusted academic writing partner</p>
            </div>
            <div className="space-y-5 text-stone-500 text-base leading-relaxed">
              <p>EssayMonk was created with one simple goal: to make academic work less stressful for students who are already juggling too much. Students understand their courses but time, pressure, and competing responsibilities often get in the way of producing well-structured, properly researched work. EssayMonk bridges that gap.</p>
              <p>We provide academic writing support across a wide range of subjects, helping students organize ideas, conduct credible research, and present their work clearly and professionally. Whether it's an essay, research paper, case study, dissertation, or editing work, our focus is always accuracy, originality, and strict adherence to instructions.</p>
              <p>EssayMonk is not about shortcuts. It's about support. We help students save time, reduce stress, and submit work they can stand behind with confidence.</p>

              <div className="grid md:grid-cols-2 gap-6 pt-4">
                <div className="rounded-2xl p-7 border-2 border-amber-200"
                  style={{ background: T.cream }}>
                  <h3 className="font-bold text-stone-900 text-lg mb-4">Why "Monk"?</h3>
                  <p className="text-sm mb-4">Monks are known for:</p>
                  {['Wisdom','Focus','Respect for Structure'].map(v => (
                    <div key={v} className="flex items-center gap-2 mb-2">
                      <span className="font-black" style={{ color: T.brown }}>✓</span>
                      <strong className="text-sm">{v}</strong>
                    </div>
                  ))}
                  <p className="text-xs text-stone-400 mt-3 leading-relaxed">
                    These are the same principles required to produce excellent academic writing.
                  </p>
                </div>

                <div className="rounded-2xl p-7 border-2 border-stone-200 bg-stone-50">
                  <h3 className="font-bold text-stone-900 text-lg mb-4">Confidentiality & Trust</h3>
                  <p className="text-sm leading-relaxed">
                    What you share with EssayMonk stays private. Your identity, your work,
                    and your communication are fully protected.
                  </p>
                  <p className="font-bold mt-4 text-sm" style={{ color: T.brown }}>
                    What happens in the Monastery stays in the Monastery.
                  </p>
                  <p className="text-xs text-stone-400 mt-4">🎓 5,000+ students helped · Since 2020</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ FAQ ═══════════════════════════════════════════ */}
        <section ref={sectionRefs.faq} className="py-24 px-6"
          style={{ background: `linear-gradient(135deg,${T.cream},#f5f5f4)` }}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span style={chipStyle}>GOT QUESTIONS?</span>
              <h2 style={sectionHeading}>Frequently Asked Questions</h2>
              <p className="text-stone-400 text-lg">We've got answers!</p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <FAQItem key={i} faq={faq} />
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-16 rounded-2xl p-12 text-center text-white overflow-hidden"
              style={{ background: `linear-gradient(135deg,${T.brown},${T.darkbr})` }}>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: T.font }}>
                Ready to get started?
              </h3>
              <p className="text-amber-100 mb-8 text-base">
                Join thousands of students who trust EssayMonk with their academic success.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button onClick={() => setCurrentPage('order')}
                  className="bg-white font-bold text-sm px-8 py-3 rounded-full hover:bg-amber-50 transition"
                  style={{ color: T.brown }}>
                  Order Essay Now
                </button>
                <button onClick={() => setCurrentPage('coursework')}
                  className="font-bold text-sm px-8 py-3 rounded-full border-2 border-white/50 text-white hover:bg-white/10 transition">
                  Get Coursework Help
                </button>
              </div>
            </div>
          </div>
        </section>

      </div>
    );
  };

  /* ── ORDER PAGE ──────────────────────────────────────────── */
  const OrderPage = () => {
    const ctx = orderContext || {};
    const [formData, setFormData] = useState({
      email: '', phone: '', topic: '', instructions: '', sources: '0', citation: 'APA',
    });
    const [attachments, setAttachments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Use context from calculator if available, else defaults
    const [level]    = useState(ctx.academicLevel || 'undergraduate');
    const [dl]       = useState(ctx.deadline      || '7days');
    const [pg]       = useState(ctx.pages         || 1);
    const displayPrice = ctx.price || calcPrice(dl, pg, level);

    const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
   
    const validFiles = files.filter(file => file.size <= 30 * 1024 * 1024);
    if (validFiles.length < files.length) {
      alert('Some files were too large (max 30MB per file)');
    }
    setAttachments(prev => [...prev, ...validFiles].slice(0, 10));
    };

    const removeFile = (index) => {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    };


    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.email || !formData.topic || !formData.instructions) {
        alert('Please fill in all required fields'); return;
      }
      setIsSubmitting(true);
      try {
      
        const formDataToSend = new FormData();
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('academic_level', level);
        formDataToSend.append('pages', pg);
        formDataToSend.append('deadline', dl);
        formDataToSend.append('topic', formData.topic);
        formDataToSend.append('instructions', formData.instructions);
        formDataToSend.append('sources', formData.sources);
        formDataToSend.append('citation_style', formData.citation);
      
      // Append files
        attachments.forEach((file, index) => {
          formDataToSend.append(`attachment_${index}`, file);
        });
    
        const orderResponse   = await createEssayOrder(formDataToSend);
        const paymentResponse = await createPayment(orderResponse.order_id);
        if (paymentResponse.approval_url) window.location.href = paymentResponse.approval_url;
        else alert('Error: No payment URL received');
      } catch (error) {
        alert('Error: ' + (error.error || error.message || 'Please try again'));
        setIsSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen py-16 px-6"
        style={{ background: `linear-gradient(135deg,${T.cream},#f5f5f4)` }}>
        <div className="max-w-2xl mx-auto">
          <button onClick={() => scrollTo('hero')}
            className="flex items-center gap-1 text-sm font-semibold mb-8 hover:underline"
            style={{ background: 'none', border: 'none', color: T.brown, cursor: 'pointer' }}>
            ← Back to Home
          </button>

          <h1 className="text-3xl font-bold text-center mb-6" style={{ fontFamily: T.font }}>
            Order Your Essay
          </h1>

          {/* Summary */}
          <div className="bg-white rounded-xl px-6 py-4 border-2 border-amber-200 mb-6 flex flex-wrap justify-between items-center gap-3">
            <span className="text-sm text-stone-400 capitalize">
              {level} · {pg} page{pg !== 1 ? 's' : ''} · {dl.replace('hrs',' hrs').replace('days',' days')}
            </span>
            <span className="font-black text-2xl tabular-nums" style={{ color: T.brown, fontFamily: T.font }}>
              ${displayPrice}
            </span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="space-y-5">
              {[
                { label:'Email Address *',       key:'email',  type:'email', ph:'your.email@example.com' },
                { label:'Phone Number',           key:'phone',  type:'tel',   ph:'Optional — for urgent contact' },
                { label:'Essay Topic / Title *',  key:'topic',  type:'text',  ph:'Enter your essay topic' },
              ].map(({ label, key, type, ph }) => (
                <div key={key}>
                  <label className="block text-sm font-semibold text-stone-600 mb-1.5">{label}</label>
                  <input type={type} value={formData[key]}
                    onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                    style={inpStyle()} placeholder={ph} />
                </div>
              ))}
              <div>
                <label className="block text-sm font-semibold text-stone-600 mb-1.5">Detailed Instructions *</label>
                <textarea rows="6" value={formData.instructions}
                  onChange={e => setFormData({ ...formData, instructions: e.target.value })}
                  style={{ ...inpStyle(), resize: 'vertical' }}
                  placeholder="Provide as many details as possible…" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-stone-600 mb-1.5">Number of Sources</label>
                  <select value={formData.sources}
                    onChange={e => setFormData({ ...formData, sources: e.target.value })}
                    style={inpStyle()}>
                    <option value="0">No specific requirement</option>
                    <option value="3">3 sources</option>
                    <option value="5">5 sources</option>
                    <option value="10">10 sources</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-600 mb-1.5">Citation Style</label>
                  <select value={formData.citation}
                    onChange={e => setFormData({ ...formData, citation: e.target.value })}
                    style={inpStyle()}>
                    {['APA','MLA','Harvard','Chicago','Other'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-600 mb-1.5">
                  Attachments (Optional)
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{
                    ...inpStyle(),
                    padding: '8px 12px',
                    cursor: 'pointer',
                  }}
                />
                <p className="text-xs text-stone-400 mt-1">
                  Max 10 files, 30MB each. Accepted: PDF, DOC, DOCX, TXT, images
                </p>
              
                {/* Display uploaded files */}
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-stone-50 rounded-lg px-4 py-2">
                        <span className="text-sm text-stone-600 truncate flex-1">
                          📎 {file.name} ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 text-sm font-bold ml-3"
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                        ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={handleSubmit} disabled={isSubmitting}
                className="w-full py-4 rounded-xl font-bold text-lg text-white transition shadow-lg"
                style={{
                  background: isSubmitting ? '#a8a29e' : `linear-gradient(135deg,${T.brown},${T.darkbr})`,
                  border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}>
                {isSubmitting ? 'Processing…' : `Proceed to Payment ($${displayPrice})`}
              </button>
              <p className="text-center text-xs text-stone-300">🔒 Secure payment powered by PayPal</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ── FOOTER ──────────────────────────────────────────────── */
  const Footer = () => (
    <footer className="py-14 px-6" style={{ background: '#0c0a09', color: '#fff' }}>
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">📚</span>
            <span className="font-bold text-lg" style={{ fontFamily: T.font }}>EssayMonk</span>
          </div>
          <p className="text-stone-500 text-sm leading-relaxed">
            Your trusted partner for academic success since 2020.
          </p>
        </div>
        <div>
          <h4 className="font-bold text-sm mb-4">Services</h4>
          {['Essays','Research Papers','Dissertations','Coursework Help'].map(s => (
            <p key={s} className="text-stone-500 text-sm mb-2">{s}</p>
          ))}
        </div>
        <div>
          <h4 className="font-bold text-sm mb-4">Navigate</h4>
          {[['hero','Home'],['services','Services'],['how-it-works','How It Works'],
            ['about','About Us'],['faq','FAQ']].map(([key, label]) => (
            <p key={key} onClick={() => { setCurrentPage('home'); scrollTo(key); }}
              className="text-stone-500 text-sm mb-2 cursor-pointer hover:text-white transition">
              {label}
            </p>
          ))}
        </div>
        <div>
          <h4 className="font-bold text-sm mb-4">Contact</h4>
          {[
            [<Mail size={13}/>, 'support@essaymonk.com', null],
            [<X size={13}/>, '@roamingprof', 'https://x.com/roamingprof'],
            [<Instagram size={13}/>, '@essay_monk', 'https://www.instagram.com/essay_monk'],
            [<Phone size={13}/>, '+1 9144147215', null],
            [<MessageCircle size={13}/>, '24/7 Live Chat', null],
          ].map(([icon, text, href], i) => (
            <div key={i} className="flex items-center gap-2 text-stone-500 text-sm mb-2">
              {icon}
              {href
                ? <a href={href} target="_blank" rel="noopener noreferrer"
                    className="hover:text-white transition">{text}</a>
                : <span>{text}</span>}
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-stone-900 mt-10 pt-6 text-center text-stone-600 text-xs">
        © 2026 EssayMonk. All rights reserved.
      </div>
    </footer>
  );

  /* ── RENDER ──────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: T.font }}>
      <NavBar />
      {currentPage === 'home'       && <HomePage />}
      {currentPage === 'order'      && <OrderPage />}
      {currentPage === 'coursework' && <CourseworkPage scrollTo={scrollTo} />}
      <Footer />
      <FloatingWhatsApp
        phoneNumber="+19144147215"
        accountName="Essaymonk Support Team"
        chatMessage="Hello! How can we assist you with your academic needs today?"
      />
    </div>
  );
}

/* ── COURSEWORK PAGE ─────────────────────────────────────── */
function CourseworkPage({ scrollTo }) {
  const [localEmail, setLocalEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courseworkDetails, setCourseworkDetails] = useState({
    courseName: '', duration: '', assignments: '', additionalInfo: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await createCourseworkInquiry({ email: localEmail, ...courseworkDetails });
      alert('Submitted! You will receive a quote via email within 2 hours.');
      // Reset form
      setLocalEmail('');
      setCourseworkDetails({ courseName: '', duration: '', assignments: '', additionalInfo: '' });
    } catch (err) {
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const T = {
    brown: '#92400e', darkbr: '#78350f',
    font: '"Palatino Linotype","Book Antiqua",Palatino,Georgia,serif',
  };

  const inpStyle = (extra = {}) => ({
    width: '100%', padding: '12px 16px',
    border: '2px solid #e7e5e4', borderRadius: 10,
    fontSize: 15, color: '#1c1917', outline: 'none',
    boxSizing: 'border-box', background: '#fff', ...extra,
  });

  return (
    <div className="min-h-screen py-16 px-6"
      style={{ background: `linear-gradient(135deg,#fffbeb,#f5f5f4)` }}>
      <div className="max-w-2xl mx-auto">
        <button onClick={() => scrollTo('hero')}
          className="text-sm font-semibold mb-8 flex items-center gap-1 hover:underline"
          style={{ background: 'none', border: 'none', color: T.brown, cursor: 'pointer' }}>
          ← Back to Home
        </button>
        <h1 className="text-3xl font-bold text-center mb-3" style={{ fontFamily: T.font }}>
          Coursework Assistance
        </h1>
        <p className="text-center text-stone-400 mb-8">Complete course management tailored to your needs</p>

        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-base mb-2">📋 Coursework Pricing</h3>
          <p className="text-stone-500 text-sm leading-relaxed">
            Billed based on complexity and workload. We'll send a custom quote within 2 hours.
            <br /><strong>Typical pricing:</strong> $500–$2,000 per course.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-stone-600 mb-1.5">Your Email *</label>
              <input type="email" required value={localEmail}
                onChange={e => setLocalEmail(e.target.value)}
                style={inpStyle()} placeholder="your.email@example.com" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-600 mb-1.5">Course Name *</label>
              <input type="text" required value={courseworkDetails.courseName}
                onChange={e => setCourseworkDetails(prev => ({ ...prev, courseName: e.target.value }))}
                style={inpStyle()} placeholder="e.g., Business Statistics 101" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-600 mb-1.5">Course Duration *</label>
              <select required value={courseworkDetails.duration}
                onChange={e => setCourseworkDetails(prev => ({ ...prev, duration: e.target.value }))}
                style={inpStyle()}>
                <option value="">Select duration</option>
                <option value="4weeks">4 weeks</option>
                <option value="8weeks">8 weeks</option>
                <option value="semester">Full semester</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-600 mb-1.5">Number of Assignments *</label>
              <input type="text" required value={courseworkDetails.assignments}
                onChange={e => setCourseworkDetails(prev => ({ ...prev, assignments: e.target.value }))}
                style={inpStyle()} placeholder="e.g., 10 weekly assignments" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-600 mb-1.5">Additional Information</label>
              <textarea rows="5" value={courseworkDetails.additionalInfo}
                onChange={e => setCourseworkDetails(prev => ({ ...prev, additionalInfo: e.target.value }))}
                style={{ ...inpStyle(), resize: 'vertical' }}
                placeholder="Course details, target grade, etc." />
            </div>
            <button type="submit" disabled={isSubmitting}
              className="w-full py-4 rounded-xl font-bold text-lg text-white transition shadow-lg"
              style={{
                background: isSubmitting ? '#a8a29e' : `linear-gradient(135deg,${T.brown},${T.darkbr})`,
                border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}>
              {isSubmitting ? 'Submitting…' : 'Submit for Custom Quote'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
/* ── FAQ accordion item (separate component avoids closure issues) ── */
function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-2 border-stone-200 rounded-2xl overflow-hidden bg-white">
      <button onClick={() => setOpen(o => !o)}
        className="w-full px-6 py-5 flex justify-between items-center text-left transition"
        style={{ background: open ? '#fffbeb' : '#fff', border: 'none', cursor: 'pointer' }}>
        <span className="font-bold text-stone-800">{faq.q}</span>
        <span className="text-xl font-bold ml-4 flex-shrink-0" style={{ color: '#92400e' }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 text-stone-500 text-sm leading-relaxed"
          style={{ background: '#fffbeb' }}>
          {faq.a}
        </div>
      )}
    </div>
  );
}